import React from 'react'
import { get, keyBy } from 'lodash'
import Html from 'src/components/Html/Html'
import {
  formatDate,
  formatMillion,
  formatPercentage,
  formatWithCommas,
} from '../../utils/functions'
import FourColumnsDatapoints from '../FourColumnsDatapoints'
import AsOfDate from '../AsOfDate'

export default function EtfFundFacts({ data = {}, fundClass, popups }) {
  if (data === null) {
    data = {}
  }

  const {
    data: {
      etfNavBasket,
      dividendFrequency,
      current30DayUnsubsidizedYield,
      etfLiquidity,
    },
  } = { data }

  const { expense_ratio, inception_date, ticker, etf_product_facts } = {
    ...fundClass,
  }

  const etf_facts = get(etf_product_facts, '[0]')
  const asOfDate = get(data, 'valuation.calendar.date')
  const dividendYield = get(data, 'fundComposite.dividendYield')
  const popovers = keyBy(popups, (p) => p.field_name)
  const { characteristics = [] } = etf_facts ?? {}

  // Booleans used to decide whether a row should have white or blue background
  const opt_1 = expense_ratio && expense_ratio.adjusted
  const opt_3 = etfNavBasket && etfNavBasket.calendar.date > inception_date

  // Note for later: use {formatDate(etfNavBasket.calendar.date)} for 'as of' date,
  // move to inline for tna + shares

  const datapoints = [
    {
      label: 'Inception Date',
      value: formatDate(inception_date),
    },
    {
      label: 'Expense Ratio (%)',
      value: expense_ratio ? expense_ratio.net : '--',
      labelHasPopover:
        expense_ratio &&
        expense_ratio.net_popup &&
        expense_ratio.net_popup.length === 1 &&
        expense_ratio.net_popup[0].html,
      popover: {
        htmlContent:
          expense_ratio &&
          expense_ratio.net_popup &&
          expense_ratio.net_popup.length === 1 &&
          expense_ratio.net_popup[0].html &&
          expense_ratio.net_popup[0].html.map((html, index) => (
            <p key={`expense-ratio-${index}`}>
              <Html content={html} nowrap={true} />
            </p>
          )),
        text:
          expense_ratio &&
          expense_ratio.net_popup &&
          expense_ratio.net_popup.length === 1 &&
          expense_ratio.net_popup[0].text,
      },
    },
  ]

  if (opt_1) {
    datapoints.push({
      label: 'Adjusted Expense Ratio',
      value: expense_ratio ? expense_ratio.adjusted : '--',
      labelHasPopover:
        expense_ratio &&
        expense_ratio.adjusted_popup &&
        expense_ratio.adjusted_popup.length === 1 &&
        expense_ratio.adjusted_popup[0].html,
      popover: {
        htmlContent:
          expense_ratio &&
          expense_ratio.net_popup &&
          expense_ratio.net_popup.length === 1 &&
          expense_ratio.adjusted_popup[0].html &&
          expense_ratio.adjusted_popup[0].html.map((html, index) => (
            <p key={`expense-ratio-${index}`}>
              <Html content={html} nowrap={true} />
            </p>
          )),
        text:
          expense_ratio &&
          expense_ratio.net_popup &&
          expense_ratio.net_popup.length === 1 &&
          expense_ratio.adjusted_popup[0].text,
      },
    })
  }

  if (opt_3) {
    datapoints.push({
      label: 'Total Net Assets (million)',
      value: `$${formatMillion(etfNavBasket.tna, 3)}`,
    })
    datapoints.push({
      label: 'Shares Outstanding',
      labelHasAsOfDate: true,
      labelAsOfDate: `(As of ${formatDate(etfNavBasket.calendar.date)})`,
      value: `${formatWithCommas(etfNavBasket.shares)}`,
    })
  }

  datapoints.push({
    label: 'Ticker',
    value: ticker || '--',
  })

  datapoints.push({
    label: 'Cusip',
    value: (etf_facts && etf_facts.cusip) || '--',
  })

  datapoints.push({
    label: 'Exchange',
    value: (etf_facts && etf_facts.exchange) || '--',
  })

  datapoints.push({
    label: 'ISIN',
    value: (etf_facts && etf_facts.isin) || '--',
    valueFontSize: '24px',
    valueFontWeight: '500',
  })

  datapoints.push({
    label: 'Distributor',
    labelHasPopover: popovers && popovers.etf_distributor,
    popover:
      popovers && popovers.etf_distributor
        ? {
            content: popovers.etf_distributor.content,
            className: 'icon-trigger in-table--trigger',
            header: popovers.etf_distributor.title,
          }
        : {},
    value: (etf_facts && etf_facts.etf_distributor) || '--',
    valueFontSize: '24px',
    valueFontWeight: '500',
  })

  datapoints.push({
    label: 'Dividend Frequency',
    value: dividendFrequency || '--',
  })

  datapoints.push({
    label: 'Morningstar Category',
    value: (etf_facts && etf_facts.morningstar_category) || '--',
    valueFontSize: '24px',
    valueFontWeight: '500',
  })

  if (etf_facts) {
    if (etf_facts.sedol) {
      datapoints.push({
        label: 'Sedol',
        value: etf_facts.sedol,
      })
    }

    if (etf_facts.benchmark) {
      datapoints.push({
        label: 'Benchmark',
        value: etf_facts.benchmark || '--',
        valueFontSize: '24px',
        valueFontWeight: '500',
      })
    }
  }

  if (current30DayUnsubsidizedYield?.current30DayUnsubsidized) {
    datapoints.push({
      label: 'SEC 30-Day Unsubsidized Yield',
      labelHasAsOfDate: true,
      labelAsOfDate: `(As of ${formatDate(
        current30DayUnsubsidizedYield.calendar.monthEndingDate
      )})`,
      labelHasPopover: popovers && popovers.current30DayUnsubsidized,
      popover:
        popovers && popovers.current30DayUnsubsidized
          ? {
              content: popovers.current30DayUnsubsidized.content,
              className: 'icon-trigger in-table--trigger',
              header: '',
            }
          : {},
      value: `${formatPercentage(
        current30DayUnsubsidizedYield.current30DayUnsubsidized
      )}`,
    })
  }

  if (dividendYield?.fundValue) {
    datapoints.push({
      label: 'Dividend Yield',
      labelHasAsOfDate: true,
      labelAsOfDate: `(As of ${formatDate(dividendYield.calendarDate)})`,
      labelHasPopover: popovers && popovers.dividendYield,
      popover:
        popovers && popovers.dividendYield
          ? {
              content: popovers.dividendYield.content,
              className: 'icon-trigger in-table--trigger',
              header: '',
            }
          : {},
      value: `${formatPercentage(dividendYield.fundValue / 100)}`,
    })
  }

  characteristics.forEach((charItem) => {
    const { title, value, as_of_date, popup = [] } = charItem
    const [popupObj = {}] = popup
    const { content: popupContent = [] } = popupObj
    const [popupContentText] = popupContent

    if (title && value && as_of_date) {
      if (popupContentText) {
        datapoints.push({
          label: title,
          labelHasAsOfDate: true,
          labelAsOfDate: `(As of ${formatDate(as_of_date)})`,
          labelHasPopover: true,
          popover: {
            content: popupContentText,
            header: '',
            className: 'icon-trigger in-table--trigger',
          },
          value,
        })
      } else {
        datapoints.push({
          label: title,
          labelHasAsOfDate: true,
          labelAsOfDate: `(As of ${formatDate(as_of_date)})`,
          value,
        })
      }
    }
  })

  if (etfLiquidity) {
    if (etfLiquidity.implied_liquidity_shares) {
      datapoints.push({
        label: 'Implied Liquidity™ (Shares)',
        value: `${formatWithCommas(etfLiquidity.implied_liquidity_shares)}`,
        labelHasPopover: popovers && popovers.implied_liquidity_shares,
        popover:
          popovers && popovers.implied_liquidity_shares
            ? {
                content: popovers.implied_liquidity_shares.content,
                className: 'icon-trigger in-table--trigger',
                header: '',
              }
            : {},
      })
    }

    if (etfLiquidity.implied_liquidity_dollars) {
      datapoints.push({
        label: 'Implied Liquidity',
        value: `$${formatWithCommas(
          etfLiquidity.implied_liquidity_dollars,
          2
        )}`,
        labelHasPopover: popovers && popovers.implied_liquidity_dollars,
        popover:
          popovers && popovers.implied_liquidity_dollars
            ? {
                content: popovers.implied_liquidity_dollars.content,
                className: 'icon-trigger in-table--trigger',
                header: '',
              }
            : {},
      })
    }
  }

  while (datapoints.length % 4 !== 0) {
    datapoints.push({})
  }

  datapoints.push({
    label: 'Investment Objective',
    value: (etf_facts && etf_facts.investment_objective) || '--',
    valueFontSize: '24px',
    valueFontWeight: '500',
  })

  let tempArray = []

  return (
    <div>
      <AsOfDate date={asOfDate} />
      {datapoints.map((datapoint, index) => {
        if (index % 4 === 0) {
          tempArray = []
          tempArray.push(datapoint)
        } else {
          tempArray.push(datapoint)
        }

        if (tempArray.length === 4 || index === datapoints.length - 1) {
          return <FourColumnsDatapoints datapoints={tempArray} key={index} />
        }

        return <div key={index} />
      })}
    </div>
  )
}